/* ==========================================================================
   oddit-section-product-recommendations
   Tab switching, horizontal scroll, and Product Recommendations API fetch
   ========================================================================== */

(function () {
  'use strict';

  /* Recently Viewed — cookie helpers
     Shares the cookie name/format with the base theme (Shopify.Products)
     so history recorded by either implementation is visible to both.
     ======================================================================== */

  var RECENTLY_VIEWED_COOKIE = 'shopify_recently_viewed';
  var RECENTLY_VIEWED_MAX = 10;

  function readRecentlyViewed() {
    var row = document.cookie.split('; ').find(function (r) {
      return r.indexOf(RECENTLY_VIEWED_COOKIE + '=') === 0;
    });
    if (!row) return [];
    var value = decodeURIComponent(row.split('=')[1] || '');
    return value ? value.split(' ') : [];
  }

  function recordRecentlyViewed(handle) {
    if (!handle) return;
    var handles = readRecentlyViewed();
    var position = handles.indexOf(handle);
    if (position !== -1) handles.splice(position, 1);
    handles.unshift(handle);
    handles = handles.slice(0, RECENTLY_VIEWED_MAX);

    var expires = new Date(Date.now() + 90 * 24 * 3600 * 1000).toGMTString();
    document.cookie =
      RECENTLY_VIEWED_COOKIE + '=' + encodeURIComponent(handles.join(' ')) +
      '; expires=' + expires +
      '; path=/; domain=' + window.location.hostname +
      '; sameSite=none; secure';
  }

  /**
   * Populate the "Recently viewed" panel with cards fetched via the
   * oddit-api-product-card section.
   * @param {HTMLElement} section
   * @param {string} currentHandle - Handle of the product being viewed (excluded).
   */
  function populateRecentlyViewed(section, currentHandle) {
    var panel = section.querySelector('[data-panel="recently-viewed"]');
    if (!panel) return;
    var grid = panel.querySelector('.oddit-section-product-recommendations__grid');
    if (!grid) return;

    var limit = parseInt(section.dataset.productsToShow, 10) || 5;
    var handles = readRecentlyViewed()
      .filter(function (handle) { return handle && handle !== currentHandle; })
      .slice(0, limit);
    if (!handles.length) return; // keep the empty-state message

    Promise.all(
      handles.map(function (handle) {
        return fetch(window.Shopify.routes.root + 'products/' + handle + '?section_id=oddit-api-product-card')
          .then(function (response) { return response.ok ? response.text() : ''; })
          .catch(function () { return ''; });
      })
    ).then(function (results) {
      var inserted = 0;

      results.forEach(function (html) {
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var content = doc.querySelector('[data-api-content]');
        if (!content || !content.firstElementChild) return;
        while (content.firstChild) {
          grid.appendChild(content.firstChild);
        }
        inserted++;
      });

      if (!inserted) return;

      var emptyMsg = grid.querySelector('.oddit-section-product-recommendations__empty');
      if (emptyMsg) emptyMsg.remove();

      bindWishlist(section);
      bindSwatches(section);
      bindDragScroll(section);
      if (typeof window.odditInitProductCards === 'function') {
        window.odditInitProductCards(section);
      }
      if (typeof window.odditInitCarouselArrows === 'function') {
        window.odditInitCarouselArrows(section);
      }
    });
  }

  /**
   * Initialize a single product-recommendations section.
   * @param {HTMLElement} section - The section root element.
   */
  function initSection(section) {
    if (!section) return;

    /* Recently Viewed — record this product, then fill the tab
       ====================================================================== */

    var productHandle = section.dataset.productHandle;
    recordRecentlyViewed(productHandle);
    populateRecentlyViewed(section, productHandle);

    /* Fetch Product Recommendations via Section Rendering API
       ====================================================================== */

    var productId = section.dataset.productId;
    var sectionId = section.dataset.sectionId;
    var intent = section.dataset.intent || 'related';
    var limit = section.dataset.productsToShow || 5;

    if (productId && sectionId) {
      var url =
        window.Shopify.routes.root +
        'recommendations/products?product_id=' +
        productId +
        '&limit=' +
        limit +
        '&intent=' +
        intent +
        '&section_id=' +
        sectionId;

      fetch(url)
        .then(function (response) {
          if (!response.ok) throw new Error('Recommendations fetch failed');
          return response.text();
        })
        .then(function (html) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');
          var freshSection = doc.querySelector('.oddit-section-product-recommendations');

          if (freshSection) {
            var recPanel = section.querySelector('[data-panel="recommendations"]');
            var freshPanel = freshSection.querySelector('[data-panel="recommendations"]');

            if (recPanel && freshPanel) {
              recPanel.innerHTML = freshPanel.innerHTML;
            }

            // Re-bind interactive elements after DOM update
            bindWishlist(section);
            bindSwatches(section);
            bindDragScroll(section);
            if (typeof window.odditInitCarouselArrows === 'function') {
              window.odditInitCarouselArrows(section);
            }
            // Per-card image carousel arrows (prev/next inside each card) are
            // initialized by oddit-collection-product-card.js. New AJAX-loaded
            // cards don't get that init unless we call it explicitly.
            if (typeof window.odditInitProductCards === 'function') {
              window.odditInitProductCards(section);
            }
          }
        })
        .catch(function (err) {
          console.error('Product recommendations error:', err);
        });
    }

    /* Tab Switching
       ====================================================================== */

    var tabs = section.querySelectorAll('.oddit-section-product-recommendations__tab');
    var panels = section.querySelectorAll('.oddit-section-product-recommendations__panel');

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var targetPanel = tab.getAttribute('data-tab');

        // Update tab active states
        tabs.forEach(function (t) {
          t.classList.remove('oddit-section-product-recommendations__tab--active');
          t.setAttribute('aria-selected', 'false');
          t.setAttribute('tabindex', '-1');
        });
        tab.classList.add('oddit-section-product-recommendations__tab--active');
        tab.setAttribute('aria-selected', 'true');
        tab.setAttribute('tabindex', '0');

        // Update panel visibility
        panels.forEach(function (panel) {
          if (panel.getAttribute('data-panel') === targetPanel) {
            panel.classList.add('oddit-section-product-recommendations__panel--active');
            panel.removeAttribute('hidden');
          } else {
            panel.classList.remove('oddit-section-product-recommendations__panel--active');
            panel.setAttribute('hidden', '');
          }
        });
      });
    });

    /* Keyboard Navigation for Tabs
       ====================================================================== */

    tabs.forEach(function (tab, index) {
      tab.addEventListener('keydown', function (e) {
        var targetIndex = -1;

        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          targetIndex = (index + 1) % tabs.length;
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          targetIndex = (index - 1 + tabs.length) % tabs.length;
        }

        if (targetIndex >= 0) {
          tabs[targetIndex].focus();
          tabs[targetIndex].click();
        }
      });
    });

    /* Bind interactive elements
       ====================================================================== */

    bindWishlist(section);
    bindSwatches(section);
    bindDragScroll(section);
  }

  /**
   * Bind wishlist toggle buttons within a section.
   * @param {HTMLElement} section
   */
  function bindWishlist(section) {
    var wishlistButtons = section.querySelectorAll('.oddit-section-product-recommendations__wishlist');

    wishlistButtons.forEach(function (btn) {
      // Prevent duplicate listeners by marking bound buttons
      if (btn.dataset.bound) return;
      btn.dataset.bound = 'true';

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var svg = btn.querySelector('svg path');
        var isActive = btn.classList.toggle('oddit-section-product-recommendations__wishlist--active');

        if (svg) {
          svg.setAttribute('fill', isActive ? '#971534' : 'none');
          svg.setAttribute('stroke', isActive ? '#971534' : '#000');
        }
      });
    });
  }

  /**
   * Bind color swatch buttons within a section.
   * @param {HTMLElement} section
   */
  function bindSwatches(section) {
    var swatchGroups = section.querySelectorAll('.oddit-section-product-recommendations__card-swatches');

    swatchGroups.forEach(function (group) {
      var swatches = group.querySelectorAll('.oddit-section-product-recommendations__card-swatch');

      swatches.forEach(function (swatch) {
        // Prevent duplicate listeners
        if (swatch.dataset.bound) return;
        swatch.dataset.bound = 'true';

        swatch.addEventListener('click', function (e) {
          e.preventDefault();
          swatches.forEach(function (s) {
            s.classList.remove('oddit-section-product-recommendations__card-swatch--active');
          });
          swatch.classList.add('oddit-section-product-recommendations__card-swatch--active');
        });
      });
    });
  }

  /**
   * Drag-to-scroll on product grids (mouse only — touch scrolls natively).
   * @param {HTMLElement} section
   */
  function bindDragScroll(section) {
    var grids = section.querySelectorAll('.oddit-section-product-recommendations__grid');

    grids.forEach(function (grid) {
      if (grid._dragBound) return;
      grid._dragBound = true;

      // Always start the carousel at the first card on load
      requestAnimationFrame(function () { grid.scrollLeft = 0; });

      var isDown = false;
      var startX;
      var scrollLeft;
      var didDrag = false;

      grid.addEventListener('dragstart', function (e) {
        e.preventDefault();
      });

      grid.addEventListener('mousedown', function (e) {
        if (e.button !== 0) return;
        // Skip drag for swatches and wishlist buttons — let their clicks work normally
        if (e.target.closest('.oddit-section-product-recommendations__card-swatch, .oddit-section-product-recommendations__wishlist')) return;
        isDown = true;
        didDrag = false;
        startX = e.pageX;
        scrollLeft = grid.scrollLeft;
      });

      document.addEventListener('mousemove', function (e) {
        if (!isDown) return;
        var walk = e.pageX - startX;
        if (Math.abs(walk) > 5) {
          didDrag = true;
          grid.classList.add('is-dragging');
        }
        if (didDrag) {
          e.preventDefault();
          grid.scrollLeft = scrollLeft - walk;
        }
      });

      document.addEventListener('mouseup', function () {
        if (!isDown) return;
        isDown = false;
        grid.classList.remove('is-dragging');
        // Clear didDrag shortly after mouseup so only the immediate
        // click event from the drag release is blocked, not later clicks
        if (didDrag) {
          setTimeout(function () { didDrag = false; }, 0);
        }
      });

      // Capture-phase listener blocks the click that fires as part of
      // the mousedown→mousemove→mouseup drag sequence
      grid.addEventListener('click', function (e) {
        if (didDrag) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);
    });
  }

  /* Initial load — initialize all instances on the page
     ======================================================================== */

  document.querySelectorAll('.oddit-section-product-recommendations').forEach(function (el) {
    initSection(el);
  });

  /* Theme Editor Compatibility
     ======================================================================== */

  if (Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      var section = e.target.querySelector('.oddit-section-product-recommendations');
      if (section) {
        initSection(section);
      }
    });
  }
})();
